/**
 * Data status: where the numbers come from and when they last updated. The daily work (what to order,
 * what's arriving) lives on Home, To order and Arriving, so this page is data health only.
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { LeadTimeSettingsResponse, TieOutResponse } from "@march/shared";
import { apiGet } from "../lib/api.ts";
import { useMe } from "../hooks/useMe.tsx";
import { theme } from "../theme.ts";

interface Ingestion {
  ok: boolean;
  runs: Array<{ job_name: string; last_started: number; status: string }>;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const when = (t: number | null | undefined) =>
  t ? new Date(t).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

export function DashboardPage() {
  const me = useMe();
  const ingestion = useQuery<Ingestion>({ queryKey: ["ingestion-health"], queryFn: () => apiGet<Ingestion>("/api/health/ingestion") });
  const tieout = useQuery<TieOutResponse>({ queryKey: ["tieout"], queryFn: () => apiGet<TieOutResponse>("/api/tieout/latest") });
  const lead = useQuery<LeadTimeSettingsResponse>({ queryKey: ["lead-settings"], queryFn: () => apiGet("/api/settings/lead-times") });

  const isAdmin = me.data?.role === "admin";
  const lastRun = (job: string) => ingestion.data?.runs?.find((r) => r.job_name === job)?.last_started;
  const month = new Date().getMonth() + 1;
  const season = lead.data?.settings.season_months ?? [];
  const inSeason = season.includes(month);
  const seasonText = season.length ? `${MONTHS[Math.min(...season) - 1]}–${MONTHS[Math.max(...season) - 1]}` : "";
  const tie = tieout.data?.latest;

  return (
    <div style={{ display: "grid", gap: theme.space.lg }}>
      <p className="tab-desc" style={{ margin: 0 }}>
        Where the numbers come from and when they last updated.
        {lead.data && <> {inSeason ? "In season" : "Off season"}{seasonText ? ` (${seasonText} is season)` : ""}.</>}
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: theme.space.md }}>
        <StatCard label="Stock synced" value={when(lastRun("inventory_snapshot"))} hint="From NetSuite, every hour" />
        <StatCard label="Forecast updated" value={when(lastRun("compute_forecast"))} hint="Nightly, about 11 PM Pacific" />
        <StatCard
          label="NetSuite tie-out"
          value={tieout.isLoading ? "…" : !tie ? "Not run yet" : tie.status === "ok" ? "Match" : tie.status === "mismatch" ? `${tie.mismatched} differ` : "Failed"}
          hint={tie ? `${tie.skus_checked} parts checked ${when(tie.run_at)}` : ""}
          highlight={!tie ? "neutral" : tie.status === "ok" ? "success" : "warning"}
        />
      </div>
      {isAdmin && <Link to="/admin" className="btn" style={{ justifySelf: "start" }}>Admin settings →</Link>}
    </div>
  );
}

function StatCard({ label, value, hint, highlight }: { label: string; value: string | number; hint?: string; highlight?: "success" | "warning" | "neutral" }) {
  const accent = highlight === "success" ? theme.color.success : highlight === "warning" ? theme.color.warning : theme.color.text;
  return (
    <div className="card">
      <div className="k">{label}</div>
      <div className="v" style={{ fontSize: 24, color: accent }}>{value}</div>
      {hint && <div className="line">{hint}</div>}
    </div>
  );
}
